<script setup lang="ts">
import type { TagProps } from 'primevue';
import { computed, toRef } from 'vue';

import VButton from '@components/buttons/VButton/VButton.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentPaymentSystem } from '@modules/payment-system-details/composables';
import { EPaymentSystemStatus } from '@modules/payment-system-details/types';

import type { IAffiliatePaymentSystemEmits, IAffiliatePaymentSystemProps } from './types';
import VContactsMenuButton from '../VContactsMenuButton/VContactsMenuButton.vue';

const props = defineProps<IAffiliatePaymentSystemProps>();
defineEmits<IAffiliatePaymentSystemEmits>();

const { getTranslation } = useI18n();

const { currentPaymentSystem } = useCurrentPaymentSystem(toRef(props, 'paymentSystemName'));

const tagSeverity = computed<TagProps['severity']>(() => {
  switch (props.status) {
    case EPaymentSystemStatus.APPROVED:
      return 'secondary';
    case EPaymentSystemStatus.PENDING:
      return 'warn';
    case EPaymentSystemStatus.REJECTED:
      return 'danger';
    default:
      return undefined;
  }
});

const tagTextValue = computed<string>(() => getTranslation(props.translateKey, `PAYMENT_SYSTEMS_STATUS.${props.status}`).value);
</script>

<template>
  <div v-data-qa-auto-id="'VAffiliatePaymentSystem'">
    <div
      v-if="!paymentSystemName"
      class="flex items-center gap-3 py-2"
    >
      <i class="pi pi-info-circle text-lg" />
      <span class="text-muted-color">
        {{ $getTranslation(translateKey, 'NO_PAYMENT_SYSTEM_CONFIGURED') }}
      </span>
    </div>
    <div
      v-else
      class="flex flex-col items-start"
    >
      <div class="flex flex-wrap items-center py-2">
        <div
          class="flex cursor-pointer items-center rounded-lg p-2 hover:bg-primary-100/20"
          :class="{ 'pointer-events-none': isLoading }"
          @click="$emit('show', paymentSystemName)"
        >
          <div
            v-if="currentPaymentSystem?.icon"
            class="flex h-10 w-14 items-center justify-center rounded-lg border bg-white"
          >
            <img :src="currentPaymentSystem?.icon">
          </div>
          <div class="px-2">
            <div class="mb-1 font-medium">
              {{ paymentSystemName }}
            </div>
            <div class="text-muted-color">
              {{ paymentSystemSubField }}
            </div>
          </div>
        </div>
        <div class="p-2">
          <PTag
            :severity="tagSeverity"
            :value="tagTextValue"
            :pt="{
              root: { class: 'justify-start max-w-80' },
              label: { class: 'truncate' },
            }"
          />
        </div>
        <div
          v-if="status !== EPaymentSystemStatus.APPROVED"
          class="ml-10 flex flex-wrap gap-2 px-2"
        >
          <VButton
            v-if="status === EPaymentSystemStatus.REJECTED"
            :label="$getTranslation(translateKey, 'CANCEL_BUTTON')"
            icon="pi-check-circle"
            :loading="isLoading"
            :disabled="disabledCancelBtn"
            outlined
            @click="$emit('cancel', paymentSystemName)"
          />
          <VButton
            v-if="status === EPaymentSystemStatus.REJECTED"
            :label="$getTranslation(translateKey, 'EDIT_BUTTON')"
            icon="pi-pencil"
            :disabled="isLoading"
            outlined
            @click="$emit('edit', paymentSystemName)"
          />
          <VContactsMenuButton
            v-if="status === EPaymentSystemStatus.PENDING || status === EPaymentSystemStatus.REJECTED"
            :translate-key="translateKey"
            :contacts="contacts"
            :disabled="isLoading"
            :button-title="$getTranslation(translateKey, 'CONTACTS_BUTTON')"
          />
        </div>
      </div>

      <VMessage
        v-if="rejectReason"
        class="my-4"
        severity="error"
      >
        {{ rejectReason }}
      </VMessage>
    </div>
  </div>
</template>
